import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Lock,
  MessageSquare,
  Pin,
  Plus,
  Trash2,
  X,
  User,
  FolderKanban,
  FileImage,
  History,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { BASE_URL } from "utils/constant";
import {
  addMemberToDesign,
  approveDesignById,
  createCommentForDesign,
  createPinForDesign,
  deleteCommentById,
  deleteDesignById,
  downloadDiagram,
  fetchCommentsForDesign,
  fetchDesignMembers,
  fetchDesignVersions,
  fetchOrganizationMembers,
  fetchPinsForDesign,
  getStatusBadgeClass,
  lockDesignById,
  replaceDesignFile,
  updatePinById,
} from "../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";

// CRA + pdfjs worker: CDN is the most reliable option across bundlers.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function looksLikePdf(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return u.includes(".pdf") || u.startsWith("data:application/pdf");
}

function normalizeFileUrl(maybeUrl) {
  if (!maybeUrl) return null;
  const u = String(maybeUrl);
  if (
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("data:") ||
    u.startsWith("blob:")
  ) {
    return u;
  }
  return `${BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
}

function buildPdfFileSource(url) {
  if (!url) return null;
  const u = String(url);
  // data: or blob: urls can be passed directly
  if (u.startsWith("data:") || u.startsWith("blob:")) return u;

  const accessToken = localStorage.getItem("access");
  // If URL looks like it has an Azure SAS token, avoid Authorization header.
  const looksLikeSas =
    u.includes("sig=") ||
    u.includes("sv=") ||
    u.includes("se=") ||
    u.includes("skoid=") ||
    u.includes("sktid=") ||
    u.includes("skt=");

  if (!looksLikeSas && accessToken) {
    return { url: u, httpHeaders: { Authorization: `Bearer ${accessToken}` } };
  }
  return { url: u };
}

function useResizeWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const update = () => setWidth(el.getBoundingClientRect().width || 0);
    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, width };
}

function displayNameFromOrgMember(m) {
  const fn = m?.user?.first_name || "";
  const ln = m?.user?.last_name || "";
  const full = `${fn} ${ln}`.trim();
  return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function UserAvatar({ member, size = "w-7 h-7" }) {
  const name = displayNameFromOrgMember(member);
  const userPic = member?.user?.userpic || member?.userpic;
  const initials = getInitials(name);

  return (
    <div
      className={`${size} rounded-full bg-purple-100 flex items-center justify-center overflow-hidden border-[1px] border-white shrink-0`}
      title={name}
    >
      {userPic ? (
        <img src={normalizeFileUrl(userPic)} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-[10px] font-bold text-purple-700">{initials}</span>
      )}
    </div>
  );
}

function useLoadAssigneesAndApprovers({ drawing, onUpdate }) {
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const [orgMembers, setOrgMembers] = useState([]);
  const [orgMembersLoading, setOrgMembersLoading] = useState(false);

  const [designMembers, setDesignMembers] = useState([]);
  const [designMembersLoading, setDesignMembersLoading] = useState(false);

  const refreshDesignMembers = async () => {
    if (!drawing?.id) return [];
    setDesignMembersLoading(true);
    try {
      const list = await fetchDesignMembers(drawing.id);
      setDesignMembers(list || []);
      const assigneeDisplay = (list || []).map(displayNameFromOrgMember).join(", ") || "—";
      onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: assigneeDisplay, designMembers: list || [] }));
      return list || [];
    } catch (err) {
      console.error("Failed to refresh design members", err);
      setDesignMembers([]);
      onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: "—", designMembers: [] }));
      return [];
    } finally {
      setDesignMembersLoading(false);
    }
  };

  // Load design members -> used for assignee display
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      setDesignMembersLoading(true);
      try {
        const list = await fetchDesignMembers(drawing.id);
        if (!mounted) return;
        setDesignMembers(list || []);
        const assigneeDisplay = (list || []).map(displayNameFromOrgMember).join(", ") || "—";
        onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: assigneeDisplay, designMembers: list || [] }));
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load design members", err);
        setDesignMembers([]);
        onUpdateRef.current?.((prev) => ({ ...(prev || drawing), assignee: "—", designMembers: [] }));
      } finally {
        if (mounted) setDesignMembersLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id]);

  // Load org members -> used for approver display and assignee picker
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setOrgMembersLoading(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          if (!mounted) return;
          setOrgMembers([]);
          onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: "—", approverMembers: [] }));
          return;
        }
        const list = await fetchOrganizationMembers(orgId);
        if (!mounted) return;
        setOrgMembers(list || []);
        const approvers = (list || []).filter((m) => m?.design_admin === true || m?.joined_as === "owner");
        const approverDisplay = approvers.map(displayNameFromOrgMember).join(", ") || "—";
        onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: approverDisplay, approverMembers: approvers }));
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load org members", err);
        setOrgMembers([]);
        onUpdateRef.current?.((prev) => ({ ...(prev || drawing), approver: "—", approverMembers: [] }));
      } finally {
        if (mounted) setOrgMembersLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return {
    orgMembers,
    orgMembersLoading,
    designMembers,
    designMembersLoading,
    refreshDesignMembers,
  };
}

export default function DrawingDetailView({
  drawing,
  onBack,
  onUpdate,
  onDelete,
  currentMember,
}) {
  const isPdf = looksLikePdf(drawing?.diagram);
  if (isPdf) {
    return (
      <PdfDetailView
        drawing={drawing}
        onBack={onBack}
        onUpdate={onUpdate}
        onDelete={onDelete}
        currentMember={currentMember}
      />
    );
  }
  return (
    <ImageDetailView
      drawing={drawing}
      onBack={onBack}
      onUpdate={onUpdate}
      onDelete={onDelete}
      currentMember={currentMember}
    />
  );
}

function useLoadCommentsAndPins({ drawing, onUpdate }) {
  // `onUpdate` is a function prop that may change identity on each render.
  // If we include it in effect deps, we can accidentally re-fetch forever.
  // Keep the latest function in a ref so fetch effects only depend on `drawing.id`.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Load comments from server when detail view mounts or when drawing id changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      const list = await fetchCommentsForDesign(drawing.id);
      if (!mounted) return;
      const mapped = (list || []).map((c, idx) => ({
        id: c.id || c.pk || `${drawing.id}-c-${idx}`,
        text: c.comment || c.text || "",
        author:
          c.created_by ||
          "Unknown",
        createdAt: c.created_at || c.createdAt || new Date().toISOString(),
        isResolved: !!c.is_resolved || !!c.isResolved || false,
      }));
      // Functional update prevents clobbering pins/versions/status due to async race.
      onUpdateRef.current?.((prev) => ({ ...(prev || drawing), comments: mapped }));
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id]);

  // Load pins from server when detail view mounts or when drawing id changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      const list = await fetchPinsForDesign(drawing.id);
      if (!mounted) return;
      const mapped = (list || []).map((p, idx) => ({
        ...p,
        id: p.id || p.pk || `${drawing.id}-p-${idx}`,
        x:
          typeof p.x_coordinate !== "undefined"
            ? Number(p.x_coordinate)
            : typeof p.x !== "undefined"
              ? Number(p.x)
              : 0,
        y:
          typeof p.y_coordinate !== "undefined"
            ? Number(p.y_coordinate)
            : typeof p.y !== "undefined"
              ? Number(p.y)
              : 0,
        comment: p.comment || p.text || "",
        status: p.status || "pending",
        author:
          (p.created_by &&
            (typeof p.created_by === "string"
              ? p.created_by
              : p.created_by.username || p.created_by.name)) ||
          (p.uploaded_by &&
            (typeof p.uploaded_by === "string"
              ? p.uploaded_by
              : p.uploaded_by.username || p.uploaded_by.name)) ||
          p.author ||
          "Unknown",
        createdAt: p.created_at || p.createdAt || new Date().toISOString(),
      }));
      // Functional update prevents clobbering comments/versions/status due to async race.
      onUpdateRef.current?.((prev) => ({ ...(prev || drawing), pins: mapped }));
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id]);
}

function PdfDetailView({ drawing, onBack, onUpdate, onDelete, currentMember }) {
  useLoadCommentsAndPins({ drawing, onUpdate });
  const { addAlert } = useAlert();
  const { orgMembers, orgMembersLoading, designMembers, designMembersLoading, refreshDesignMembers } =
    useLoadAssigneesAndApprovers({ drawing, onUpdate });

  const [activePinId, setActivePinId] = useState(null);
  const [isPinMode, setIsPinMode] = useState(false);

  const [pendingPin, setPendingPin] = useState(null);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinComment, setPinComment] = useState("");
  const [isCreatingPin, setIsCreatingPin] = useState(false);

  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isCreatingComment, setIsCreatingComment] = useState(false);

  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfError, setPdfError] = useState(null);

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const [isReplacing, setIsReplacing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const fileInputRef = useRef(null);
  const isLocked = drawing.status === "Locked";
  const isDraft = drawing.status === "Draft";
  const canDesignAdmin =
    currentMember?.design_admin === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approveDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Approved" }));
      addAlert("Drawing approved successfully", "success");
    } catch (err) {
      console.error("Approve failed", err);
      addAlert(err?.message || "Failed to approve drawing", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    try {
      setIsLocking(true);
      await lockDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Locked" }));
      setIsPinMode(false);
      addAlert("Drawing locked successfully", "success");
    } catch (err) {
      console.error("Lock failed", err);
      addAlert(err?.message || "Failed to lock drawing", "error");
    } finally {
      setIsLocking(false);
    }
  };

  const pdfUrl = normalizeFileUrl(drawing?.diagram);
  const pdfFile = useMemo(() => buildPdfFileSource(pdfUrl), [pdfUrl]);

  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [assigneeToAdd, setAssigneeToAdd] = useState("");
  const [addingAssignee, setAddingAssignee] = useState(false);

  const handleAddAssignee = async () => {
    if (!assigneeToAdd) return;
    try {
      setAddingAssignee(true);
      await addMemberToDesign(drawing.id, assigneeToAdd);
      await refreshDesignMembers();
      setShowAddAssignee(false);
      setAssigneeToAdd("");
      addAlert("Assignee added", "success");
    } catch (err) {
      console.error("Add assignee failed", err);
      addAlert(err?.message || "Failed to add assignee", "error");
    } finally {
      setAddingAssignee(false);
    }
  };

  // Reset page when switching designs/files
  useEffect(() => {
    setPdfPage(1);
    setPdfNumPages(null);
    setPdfError(null);
  }, [drawing?.id, drawing?.diagram]);

  // Fetch versions when modal opens (or design changes)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showVersionHistory || !drawing?.id) return;
      try {
        setVersionsLoading(true);
        setVersionsError(null);
        const list = await fetchDesignVersions(drawing.id);
        if (!mounted) return;
        setVersions(list);
        onUpdate((prev) => ({ ...(prev || drawing), versions: list }));
      } catch (err) {
        if (!mounted) return;
        console.error("Fetch version history failed", err);
        setVersionsError(err?.message || "Failed to fetch version history");
        setVersions([]);
      } finally {
        if (mounted) setVersionsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showVersionHistory, drawing.id]);

  const openIssuesCount = (drawing.pins || []).filter((p) => p.status === "pending").length;

  const handleViewerClick = (e) => {
    if (!isPinMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setShowPinDialog(true);
  };

  const handleCreatePin = async () => {
    if (!pendingPin || !pinComment.trim()) return;
    try {
      setIsCreatingPin(true);
      const payload = {
        x_coordinate: Math.round(pendingPin.x),
        y_coordinate: Math.round(pendingPin.y),
        comment: pinComment.trim(),
      };
      const created = await createPinForDesign(drawing.id, payload);

      const mapped = {
        id: created.id || created.pk || `p${Date.now()}`,
        x:
          typeof created.x_coordinate !== "undefined"
            ? Number(created.x_coordinate)
            : typeof created.x !== "undefined"
              ? Number(created.x)
              : pendingPin.x,
        y:
          typeof created.y_coordinate !== "undefined"
            ? Number(created.y_coordinate)
            : typeof created.y !== "undefined"
              ? Number(created.y)
              : pendingPin.y,
        comment: created.comment || created.text || pinComment.trim(),
        status: created.status || "pending",
        author:
          (created.created_by &&
            (typeof created.created_by === "string"
              ? created.created_by
              : created.created_by.username || created.created_by.name)) ||
          (created.uploaded_by &&
            (typeof created.uploaded_by === "string"
              ? created.uploaded_by
              : created.uploaded_by.username || created.uploaded_by.name)) ||
          created.author ||
          "Unknown",
        createdAt: created.created_at || created.createdAt || new Date().toISOString(),
      };

      onUpdate((prev) => ({ ...(prev || drawing), pins: [...((prev || drawing).pins || []), mapped] }));

      // refresh pins from server (ensures we're in sync)
      try {
        const list = await fetchPinsForDesign(drawing.id);
        const normalized = (list || []).map((p, idx) => ({
          ...p,
          id: p.id || p.pk || `${drawing.id}-p-${idx}`,
          x:
            typeof p.x_coordinate !== "undefined"
              ? Number(p.x_coordinate)
              : typeof p.x !== "undefined"
                ? Number(p.x)
                : 0,
          y:
            typeof p.y_coordinate !== "undefined"
              ? Number(p.y_coordinate)
              : typeof p.y !== "undefined"
                ? Number(p.y)
                : 0,
          comment: p.comment || p.text || "",
          status: p.status || "pending",
          author:
            (p.created_by &&
              (typeof p.created_by === "string"
                ? p.created_by
                : p.created_by.username || p.created_by.name)) ||
            (p.uploaded_by &&
              (typeof p.uploaded_by === "string"
                ? p.uploaded_by
                : p.uploaded_by.username || p.uploaded_by.name)) ||
            p.author ||
            "Unknown",
          createdAt: p.created_at || p.createdAt || new Date().toISOString(),
        }));
        onUpdate((prev) => ({ ...(prev || drawing), pins: normalized }));
      } catch (refreshErr) {
        console.warn("Refresh pins after create failed", refreshErr);
      }

      setPinComment("");
      setPendingPin(null);
      setShowPinDialog(false);
      setIsPinMode(false);
      addAlert("Pin added", "success");
    } catch (err) {
      console.error("Create pin failed", err);
      addAlert(err?.message || "Failed to add pin", "error");
    } finally {
      setIsCreatingPin(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      setIsCreatingComment(true);
      const created = await createCommentForDesign(drawing.id, commentText.trim());
      const mapped = {
        id: created.id || created.pk || `c${Date.now()}`,
        text: created.comment || created.text || commentText.trim(),
        author:
          created.created_by ||
          "Unknown",
        createdAt: created.created_at || created.createdAt || new Date().toISOString(),
        isResolved: !!created.is_resolved || !!created.isResolved || false,
      };
      onUpdate((prev) => ({ ...(prev || drawing), comments: [...((prev || drawing).comments || []), mapped] }));
      setCommentText("");
    } catch (err) {
      console.error("Create comment failed", err);
      addAlert(err?.message || "Failed to add comment", "error");
    } finally {
      setIsCreatingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteCommentById(commentId);
      onUpdate((prev) => ({
        ...(prev || drawing),
        comments: ((prev || drawing).comments || []).filter((c) => c.id !== commentId),
      }));
    } catch (err) {
      console.error("Delete comment failed", err);
      addAlert(err?.message || "Failed to delete comment", "error");
    }
  };

  const handleConfirmDeleteDesign = async () => {
    if (!window.confirm("Are you sure you want to delete this design?")) return;
    try {
      await deleteDesignById(drawing.id);
      if (onDelete) onDelete(drawing.id);
      addAlert("Design deleted", "success");
      onBack();
    } catch (err) {
      console.error("Delete design failed", err);
      addAlert(err?.message || "Failed to delete design", "error");
    }
  };

  const handleReplaceClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsReplacing(true);
      const replaced = await replaceDesignFile(drawing.id, file);
      const newDiagramRaw =
        replaced?.diagram || replaced?.fileUrl || replaced?.file_url || replaced?.url || drawing.diagram;
      const newDiagram = normalizeFileUrl(newDiagramRaw);
      const updated = {
        ...drawing,
        diagram: newDiagram,
        lastUpdated:
          (replaced?.last_updated_on || replaced?.lastUpdated || drawing.lastUpdated || new Date().toISOString())
            .toString()
            .split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      addAlert("File replaced successfully", "success");
    } catch (err) {
      console.error("Replace file failed", err);
      addAlert(err?.message || "Failed to replace file", "error");
    } finally {
      setIsReplacing(false);
    }
  };

  const handleResolvePin = async (pinId) => {
    try {
      await updatePinById(pinId, { status: "solved" });
      onUpdate((prev) => ({
        ...(prev || drawing),
        pins: ((prev || drawing).pins || []).map((p) =>
          p.id === pinId ? { ...p, status: "solved" } : p
        ),
      }));
      setActivePinId(null);
      addAlert("Issue marked as resolved", "success");
    } catch (err) {
      console.error("Resolve pin failed", err);
      addAlert(err?.message || "Failed to resolve pin", "error");
    }
  };

  return (
    <div className="flex-1 w-full min-h-screen flex flex-col p-4 gap-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button className="px-2 py-1 border rounded  cursor-pointer flex items-center gap-2 hover:bg-sm-accent hover:text-white" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Design</span>
              <span>{">"}</span>
              <span className="truncate">{drawing.name}</span>
            </div>
            <h1 className="text-lg font-bold truncate">{drawing.name}</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isLocked && (
            <>
              <button
                className={`px-2 py-1 border rounded flex items-center gap-2 bg-white ${isPinMode ? "bg-gray-100" : ""}`}
                onClick={() => setIsPinMode((v) => !v)}
              >
                <Pin className="w-4 h-4" /> {isPinMode ? "Cancel" : "Pin"}
              </button>
              <button
                className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
                onClick={() => setShowCommentsModal(true)}
              >
                <MessageSquare className="w-4 h-4" /> Comments
              </button>
            </>
          )}
          {canDesignAdmin && drawing.status === "Approved" && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
              onClick={handleLock}
              disabled={isLocking}
            >
              <Lock className="w-4 h-4" /> {isLocking ? "Locking..." : "Lock"}
            </button>
          )}
          <button
            className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
            onClick={() => downloadDiagram(pdfUrl, drawing.name)}
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button className="px-2 py-1 text-red-600 flex items-center gap-2" onClick={handleConfirmDeleteDesign}>
            <Trash2 className="w-4 h-4" />
          </button>
          <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}>
            {drawing.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Pages */}
        <aside className="col-span-2 border rounded bg-white min-h-0">
          <div className="px-3 py-2 border-b text-sm font-medium">Pages</div>
          <div className="p-2 space-y-2">
            {pdfFile && (
              <Document
                file={pdfFile}
                loading={<div className="text-xs text-gray-500 p-2">Loading…</div>}
                onLoadSuccess={({ numPages }) => {
                  setPdfError(null);
                  setPdfNumPages(numPages);
                }}
                onLoadError={(err) => setPdfError(err?.message || String(err))}
                onSourceError={(err) => setPdfError(err?.message || String(err))}
              >
                {pdfError && (
                  <div className="text-xs text-red-600 p-2">
                    Failed to load PDF file.
                    <div className="text-[10px] text-red-500 mt-1 break-words">{pdfError}</div>
                  </div>
                )}

                {Array.from({ length: pdfNumPages || 0 }).map((_, idx) => {
                  const pageNum = idx + 1;
                  const isActive = pageNum === pdfPage;
                  return (
                    <button
                      key={pageNum}
                      className={`w-full border rounded p-1 text-left ${isActive ? "border-purple-500" : "border-gray-200"
                        }`}
                      onClick={() => setPdfPage(pageNum)}
                      type="button"
                    >
                      <div className="text-[10px] text-gray-500 mb-1">Page {pageNum}</div>
                      <div className="bg-gray-50 rounded ">
                        <Page
                          pageNumber={pageNum}
                          width={120}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </div>
                    </button>
                  );
                })}
              </Document>
            )}
          </div>
        </aside>

        {/* Viewer */}
        <main className="col-span-7 border rounded bg-white min-h-0">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div className="text-sm font-medium">Viewer</div>
            <div className="flex items-center gap-2 text-xs">
              <button
                className="px-2 py-1 border rounded disabled:opacity-50"
                disabled={pdfPage <= 1}
                onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="text-gray-600">
                Page {pdfPage}
                {pdfNumPages ? ` / ${pdfNumPages}` : ""}
              </span>
              <button
                className="px-2 py-1 border rounded disabled:opacity-50"
                disabled={pdfNumPages ? pdfPage >= pdfNumPages : true}
                onClick={() => setPdfPage((p) => (pdfNumPages ? Math.min(pdfNumPages, p + 1) : p))}
              >
                Next
              </button>
            </div>
          </div>

          <PdfViewerSurface
            drawing={drawing}
            pdfFile={pdfFile}
            pdfPage={pdfPage}
            pdfError={pdfError}
            setPdfError={setPdfError}
            isPinMode={isPinMode}
            onSurfaceClick={handleViewerClick}
            pins={drawing.pins || []}
            activePinId={activePinId}
            setActivePinId={setActivePinId}
            handleResolvePin={handleResolvePin}
          />
        </main>

        {/* Right sidebar (Details/Actions/Issues/Version history) */}
        <aside className="col-span-3 flex flex-col gap-3 min-h-0 overflow-auto">
          <div className="border rounded p-2 bg-white">
            <div className="text-sm font-medium mb-2">Details</div>
            <div className="grid grid-cols-2 -gap-x-3 gap-y-1 text-sm">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <User className="w-4 h-4 text-gray-400" /> Uploaded:
              </div>
              <div className="font-medium text-sm">{drawing.uploadedBy}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <FolderKanban className="w-4 h-4 text-gray-400" /> Project:
              </div>
              <div className="font-medium text-sm">{drawing.projectName}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <FileImage className="w-4 h-4 text-gray-400" /> Type:
              </div>
              <div className="font-medium text-sm">{drawing.fileType}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <User className="w-4 h-4 text-gray-400" /> Assignee:
              </div>
              <div className="font-medium text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex overflow-hidden">
                    {(drawing.designMembers || []).length > 0 ? (
                      <div className="flex -space-x-1 items-center gap-2">
                        <div className="flex -space-x-1 items-center">
                          {(drawing.designMembers || []).slice(0, 3).map((m) => (
                            <UserAvatar key={m.id} member={m} />
                          ))}
                        </div>
                        {drawing.designMembers.length > 3 && (
                          <span className="text-sm p-0.5 px-1 flex flex-col items-center justify-center text-gray-400  rounded-full bg-slate-100">+{drawing.designMembers.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <span className="truncate text-gray-400">{designMembersLoading ? "Loading..." : "—"}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="p-1 border rounded bg-white shrink-0 disabled:opacity-60"
                    title="Add assignee"
                    onClick={() => setShowAddAssignee(true)}
                    disabled={!canDesignAdmin || orgMembersLoading}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-gray-400" /> Approver:
              </div>
              <div className="font-medium text-sm">
                <div className="flex -space-x-2 overflow-hidden">
                  {(drawing.approverMembers || []).length > 0 ? (
                    (drawing.approverMembers || []).map((m) => (
                      <UserAvatar key={m.id} member={m} />
                    ))
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {drawing.status !== "Locked" && (

            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500 mb-2">Actions</div>
              <div className="space-y-2">
                {!isLocked && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60"
                    disabled={isReplacing}
                    onClick={handleReplaceClick}
                  >
                    {isReplacing ? "Replacing..." : "Replace File"}
                  </button>
                )}
                {canDesignAdmin && (
                  <>
                    <button
                      className="w-full p-2 border rounded"
                      onClick={handleApprove}
                      disabled={drawing.status === "Approved" || isLocked || isApproving || isLocking}
                    >
                      {isApproving ? "Approving..." : drawing.status === "Approved" ? "Approved" : "Approve"}
                    </button>
                    {!isDraft && drawing.status === "Approved" && (
                      <button
                        className="w-full p-2 bg-purple-700 text-white rounded disabled:opacity-60"
                        onClick={handleLock}
                        disabled={isLocking}
                      >
                        {isLocking ? "Locking..." : "Lock Design"}
                      </button>
                    )}
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={handleReplaceSelected}
              />
            </div>
          )}

          {openIssuesCount > 0 && (
            <div className="border rounded p-2 bg-red-50">
              <div className="text-xs text-red-600 font-semibold">{openIssuesCount} Pending Issue(s)</div>
              <div className="text-xs text-gray-500">Resolve before locking</div>
            </div>
          )}

          <button className="w-full p-2 border rounded" onClick={() => setShowVersionHistory(true)}>
            Version History
          </button>
        </aside>
      </div>

      {/* Add assignee modal */}
      {showAddAssignee && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Add Assignee</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddAssignee(false);
                  setAssigneeToAdd("");
                }}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <select
              value={assigneeToAdd}
              onChange={(e) => setAssigneeToAdd(e.target.value)}
              className="w-full border p-2 rounded"
            >
              <option value="">{orgMembersLoading ? "Loading members..." : "Select member"}</option>
              {(orgMembers || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {displayNameFromOrgMember(m)}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() => {
                  setShowAddAssignee(false);
                  setAssigneeToAdd("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1 bg-purple-700 text-white rounded disabled:opacity-60"
                onClick={handleAddAssignee}
                disabled={!assigneeToAdd || addingAssignee}
              >
                {addingAssignee ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pin comment modal */}
      {showPinDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <h3 className="font-semibold mb-2">Add Comment Pin</h3>
            <textarea
              placeholder="Enter your comment or issue description..."
              value={pinComment}
              onChange={(e) => setPinComment(e.target.value)}
              rows={3}
              className="w-full border p-2 rounded"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                className="px-3 py-1 border rounded"
                onClick={() => {
                  setShowPinDialog(false);
                  setPendingPin(null);
                  setPinComment("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-purple-700 text-white rounded disabled:opacity-60"
                onClick={handleCreatePin}
                disabled={!pinComment.trim() || isCreatingPin}
              >
                {isCreatingPin ? "Adding..." : "Add Pin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments modal (PDF flow) */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-2xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Comments</h3>
              <button onClick={() => setShowCommentsModal(false)} type="button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="max-h-80 overflow-auto border rounded p-2 bg-gray-50">
              {(drawing.comments || []).length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6">No comments yet</div>
              ) : (
                <div className="space-y-2">
                  {(drawing.comments || []).map((c) => (
                    <div key={c.id} className="bg-white border rounded p-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate">{c.author}</div>
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{c.text}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-[10px] text-gray-400">
                            {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ""}
                          </div>
                          {/* <button
                            className="text-red-600 p-1"
                            title="Delete comment"
                            onClick={() => handleDeleteComment(c.id)}
                            type="button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="flex-1 border py-2 px-3 rounded text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                disabled={isCreatingComment}
              />
              <button
                className="px-3 py-2 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleAddComment}
                disabled={isCreatingComment || !commentText.trim()}
              >
                {isCreatingComment ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Version History</h3>
              <button onClick={() => setShowVersionHistory(false)} type="button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-auto">
              {versionsLoading && <div className="text-sm text-gray-500 p-3">Loading versions...</div>}
              {!versionsLoading && versionsError && <div className="text-sm text-red-600 p-3">{versionsError}</div>}
              {!versionsLoading && !versionsError && (versions || []).length === 0 && (
                <div className="text-sm text-gray-500 p-3">No versions found.</div>
              )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).map((version) => (
                  <div key={version.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xs font-bold bg-gray-200 px-2 py-1 rounded shrink-0">{version.version}</div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{version.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {version.uploadedBy || drawing.uploadedBy} • {version.uploadDate || drawing.lastUpdated}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(version.status)}`}>
                        {version.status}
                      </span>
                      <button
                        className="px-2 py-1 text-sm border rounded disabled:opacity-60"
                        disabled={!version.fileUrl}
                        onClick={() => downloadDiagram(version.fileUrl, `${drawing.name}_${version.version}`)}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageDetailView({ drawing, onBack, onUpdate, onDelete, currentMember }) {
  useLoadCommentsAndPins({ drawing, onUpdate });
  const { addAlert } = useAlert();
  const { orgMembers, orgMembersLoading, designMembers, designMembersLoading, refreshDesignMembers } =
    useLoadAssigneesAndApprovers({ drawing, onUpdate });

  const [activePinId, setActivePinId] = useState(null);
  const [isPinMode, setIsPinMode] = useState(false);
  const [newPinComment, setNewPinComment] = useState("");
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [commentText, setCommentText] = useState("");

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const fileInputRef = useRef(null);
  const [isCreatingPin, setIsCreatingPin] = useState(false);

  const isLocked = drawing.status === "Locked";
  const isDraft = drawing.status === "Draft";
  const openIssuesCount = (drawing.pins || []).filter((p) => p.status === "pending").length;
  const canDesignAdmin =
    currentMember?.design_admin === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;

  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [assigneeToAdd, setAssigneeToAdd] = useState("");
  const [addingAssignee, setAddingAssignee] = useState(false);

  const handleAddAssignee = async () => {
    if (!assigneeToAdd) return;
    try {
      setAddingAssignee(true);
      await addMemberToDesign(drawing.id, assigneeToAdd);
      await refreshDesignMembers();
      setShowAddAssignee(false);
      setAssigneeToAdd("");
      addAlert("Assignee added", "success");
    } catch (err) {
      console.error("Add assignee failed", err);
      addAlert(err?.message || "Failed to add assignee", "error");
    } finally {
      setAddingAssignee(false);
    }
  };

  // Fetch versions when modal opens (or design changes)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showVersionHistory || !drawing?.id) return;
      try {
        setVersionsLoading(true);
        setVersionsError(null);
        const list = await fetchDesignVersions(drawing.id);
        if (!mounted) return;
        setVersions(list);
        onUpdate((prev) => ({ ...(prev || drawing), versions: list }));
      } catch (err) {
        if (!mounted) return;
        console.error("Fetch version history failed", err);
        setVersionsError(err?.message || "Failed to fetch version history");
        setVersions([]);
      } finally {
        if (mounted) setVersionsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showVersionHistory, drawing.id]);

  const handleCanvasClick = (e) => {
    if (!isPinMode || isLocked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setShowPinDialog(true);
  };

  const handleAddPin = () => {
    (async () => {
      if (!pendingPin || !newPinComment.trim()) return;
      try {
        setIsCreatingPin(true);
        const payload = {
          x_coordinate: Math.round(pendingPin.x),
          y_coordinate: Math.round(pendingPin.y),
          comment: newPinComment.trim(),
        };
        const created = await createPinForDesign(drawing.id, payload);

        const mapped = {
          id: created.id || created.pk || `p${Date.now()}`,
          x:
            typeof created.x_coordinate !== "undefined"
              ? Number(created.x_coordinate)
              : typeof created.x !== "undefined"
                ? Number(created.x)
                : pendingPin.x,
          y:
            typeof created.y_coordinate !== "undefined"
              ? Number(created.y_coordinate)
              : typeof created.y !== "undefined"
                ? Number(created.y)
                : pendingPin.y,
          comment: created.comment || created.text || newPinComment.trim(),
          status: created.status || "pending",
          author:
            (created.created_by &&
              (typeof created.created_by === "string"
                ? created.created_by
                : created.created_by.username || created.created_by.name)) ||
            (created.uploaded_by &&
              (typeof created.uploaded_by === "string"
                ? created.uploaded_by
                : created.uploaded_by.username || created.uploaded_by.name)) ||
            created.author ||
            "Unknown",
          createdAt: created.created_at || created.createdAt || new Date().toISOString(),
        };

        onUpdate((prev) => ({ ...(prev || drawing), pins: [...((prev || drawing).pins || []), mapped] }));

        try {
          const list = await fetchPinsForDesign(drawing.id);
          const normalized = (list || []).map((p, idx) => ({
            ...p,
            id: p.id || p.pk || `${drawing.id}-p-${idx}`,
            x:
              typeof p.x_coordinate !== "undefined"
                ? Number(p.x_coordinate)
                : typeof p.x !== "undefined"
                  ? Number(p.x)
                  : 0,
            y:
              typeof p.y_coordinate !== "undefined"
                ? Number(p.y_coordinate)
                : typeof p.y !== "undefined"
                  ? Number(p.y)
                  : 0,
            comment: p.comment || p.text || "",
            status: p.status || "pending",
            author:
              (p.created_by &&
                (typeof p.created_by === "string"
                  ? p.created_by
                  : p.created_by.username || p.created_by.name)) ||
              (p.uploaded_by &&
                (typeof p.uploaded_by === "string"
                  ? p.uploaded_by
                  : p.uploaded_by.username || p.uploaded_by.name)) ||
              p.author ||
              "Unknown",
            createdAt: p.created_at || p.createdAt || new Date().toISOString(),
          }));
          onUpdate((prev) => ({ ...(prev || drawing), pins: normalized }));
        } catch (refreshErr) {
          console.warn("Refresh pins after create failed", refreshErr);
        }

        setNewPinComment("");
        setPendingPin(null);
        setShowPinDialog(false);
        setIsPinMode(false);
        addAlert("Pin added successfully", "success");
      } catch (err) {
        console.error("Create pin failed", err);
        addAlert(err?.message || "Failed to add pin", "error");
      } finally {
        setIsCreatingPin(false);
      }
    })();
  };

  const handleResolvePin = async (pinId) => {
    try {
      await updatePinById(pinId, { status: "solved" });
      onUpdate((prev) => ({
        ...(prev || drawing),
        pins: ((prev || drawing).pins || []).map((p) =>
          p.id === pinId ? { ...p, status: "solved" } : p
        ),
      }));
      setActivePinId(null);
      addAlert("Issue marked as resolved", "success");
    } catch (err) {
      console.error("Resolve pin failed", err);
      addAlert(err?.message || "Failed to resolve pin", "error");
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approveDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Approved" }));
      addAlert("Drawing approved successfully", "success");
    } catch (err) {
      console.error("Approve failed", err);
      addAlert(err?.message || "Failed to approve drawing", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    try {
      setIsLocking(true);
      await lockDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Locked" }));
      setIsPinMode(false);
      addAlert("Drawing locked successfully", "success");
    } catch (err) {
      console.error("Lock failed", err);
      addAlert(err?.message || "Failed to lock drawing", "error");
    } finally {
      setIsLocking(false);
    }
  };

  const handleAddComment = () => {
    (async () => {
      if (!commentText.trim()) return;
      try {
        const created = await createCommentForDesign(drawing.id, commentText.trim());
        const mapped = {
          id: created.id || created.pk || `c${Date.now()}`,
          text: created.comment || created.text || commentText.trim(),
          author:
            created.created_by ||
            "Unknown",
          createdAt: created.created_at || created.createdAt || new Date().toISOString(),
          isResolved: !!created.is_resolved || !!created.isResolved || false,
        };
        onUpdate((prev) => ({ ...(prev || drawing), comments: [...((prev || drawing).comments || []), mapped] }));
        setCommentText("");
        addAlert("Comment added", "success");
      } catch (err) {
        console.error("Create comment failed", err);
        addAlert(err?.message || "Failed to add comment", "error");
      }
    })();
  };

  const handleResolveComment = (commentId) => {
    onUpdate((prev) => ({
      ...(prev || drawing),
      comments: ((prev || drawing).comments || []).map((c) => (c.id === commentId ? { ...c, isResolved: true } : c)),
    }));
    addAlert("Comment resolved", "success");
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteCommentById(commentId);
      onUpdate((prev) => ({
        ...(prev || drawing),
        comments: ((prev || drawing).comments || []).filter((c) => c.id !== commentId),
      }));
    } catch (err) {
      console.error("Delete comment failed", err);
      addAlert(err?.message || "Failed to delete comment", "error");
    }
  };

  const handleConfirmDeleteDesign = async () => {
    try {
      await deleteDesignById(drawing.id);
      if (onDelete) onDelete(drawing.id);
      addAlert("Design deleted", "success");
      onBack();
    } catch (err) {
      console.error("Delete design failed", err);
      addAlert(err?.message || "Failed to delete design", "error");
    }
  };

  const handleReplaceClick = () => {
    if (isLocked) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsReplacing(true);
      const replaced = await replaceDesignFile(drawing.id, file);
      const newDiagramRaw =
        replaced?.diagram || replaced?.fileUrl || replaced?.file_url || replaced?.url || drawing.diagram;
      const newDiagram = normalizeFileUrl(newDiagramRaw);
      const updated = {
        ...drawing,
        diagram: newDiagram,
        lastUpdated:
          (replaced?.last_updated_on || replaced?.lastUpdated || drawing.lastUpdated || new Date().toISOString())
            .toString()
            .split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      addAlert("File replaced successfully", "success");
    } catch (err) {
      console.error("Replace file failed", err);
      addAlert(err?.message || "Failed to replace file", "error");
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <div className="flex-1 w-full min-h-screen flex flex-col p-4 gap-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="px-2 py-1 border rounded flex items-center gap-2 cursor-pointer hover:bg-sm-accent hover:text-white" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Design</span>
              <span>{">"}</span>
              <span>{drawing.name}</span>
            </div>
            <h1 className="text-lg font-bold">{drawing.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isLocked && (
            <button
              className={`px-2 py-1 border rounded flex items-center gap-2 bg-white ${isPinMode ? "bg-gray-200" : ""}`}
              onClick={() => setIsPinMode(!isPinMode)}
            >
              <Pin className="w-4 h-4" /> {isPinMode ? "Cancel" : "Pin"}
            </button>
          )}
          {!isLocked && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
              onClick={() => {
                const el = document.getElementById("design-comments");
                if (el && typeof el.scrollIntoView === "function") {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              type="button"
            >
              <MessageSquare className="w-4 h-4" /> Comment
            </button>
          )}
          <button
            className="px-2 py-1 border rounded bg-white"
            onClick={() => {
              const openIssues = (drawing.pins || []).filter((p) => p.status === "pending");
              if (openIssues.length > 0) openIssues.forEach((issue) => handleResolvePin(issue.id));
              else addAlert("No pending issues to resolve", "warning");
            }}
            disabled={isLocked}
          >
            <CheckCircle className="w-4 h-4 inline-block mr-2" /> Resolve Issues
          </button>
          {canDesignAdmin && drawing.status === "Approved" && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
              onClick={handleLock}
              disabled={isLocking}
            >
              <Lock className="w-4 h-4" /> {isLocking ? "Locking..." : "Lock"}
            </button>
          )}
          <button className="px-2 py-1 border rounded flex items-center gap-2 bg-white" onClick={() => downloadDiagram(drawing.diagram, drawing.name)}>
            <Download className="w-4 h-4" /> Download
          </button>
          <button className="px-2 py-1 text-red-600 flex items-center gap-2" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4" />
          </button>
          <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}>{drawing.status}</span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-9 flex flex-col gap-4 min-w-0">
          <div className="w-full border rounded min-h-[300px] bg-white p-6">
            <div
              className={`w-full h-full bg-gray-50 flex items-center justify-center relative ${isPinMode ? "cursor-crosshair" : ""}`}
              onClick={handleCanvasClick}
            >
              <div className="absolute inset-4 border-2 border-dashed rounded-lg flex items-center justify-center bg-white">
                {drawing.diagram ? (
                  <img src={drawing.diagram} alt={drawing.name} className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center p-6">
                    <div className="h-12 w-12 mx-auto mb-2 text-2xl">🖼️</div>
                    <p className="text-gray-500 text-sm">{drawing.name}</p>
                  </div>
                )}
              </div>

              {(drawing.pins || []).map((pin) => (
                <div
                  key={pin.id}
                  className="absolute transform -translate-x-1/2 -translate-y-full z-10"
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                >
                  <button
                    className="relative group"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePinId(activePinId === pin.id ? null : pin.id);
                    }}
                    type="button"
                  >
                    <Pin className={`w-4 h-4 ${pin.status === "solved" ? "text-green-500" : "text-red-500"}`} />
                    {activePinId === pin.id && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 z-20">
                        <div className="bg-white border p-2 rounded shadow">
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">Pin</span>
                              {pin.status === "solved" ? (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">SOLVED</span>
                              ) : (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">PENDING</span>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePinId(null);
                              }}
                              type="button"
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                          <p className="text-xs mb-1.5">{pin.comment}</p>
                          <div className="text-xs text-gray-500 space-y-0.5">
                            <p>By: {pin.author}</p>
                          </div>
                          {pin.status === "pending" && !isLocked && (
                            <button
                              className="w-full mt-2 p-1 bg-green-600 text-white rounded text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResolvePin(pin.id);
                              }}
                              type="button"
                            >
                              Resolve
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              ))}

              {isPinMode && <div className="absolute top-3 left-3 bg-purple-50 text-purple-700 px-2.5 py-1 rounded text-xs">Click to place pin</div>}
            </div>
          </div>

          {!isLocked && (
            <div className="border rounded bg-white" id="design-comments">
              <div className="px-3 py-2 border-b">
                <strong>Comments</strong>
              </div>
              <div className="p-2 max-h-44 overflow-auto">
                {(drawing.comments || []).length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No comments yet</p>
                ) : (
                  (drawing.comments || []).map((comment) => (
                    <div
                      key={comment.id}
                      className={`px-3 py-2 rounded mb-2 text-sm ${comment.isResolved ? "bg-gray-100" : "bg-gray-50"}`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <div className="font-medium text-sm">{comment.author}</div>
                          <p className="text-xs text-gray-500 mt-1">{comment.text}</p>
                        </div>
                        <div className="text-right ml-4 flex flex-col items-end gap-1">
                          <div className="text-xs text-gray-500">{new Date(comment.createdAt).toLocaleDateString()}</div>
                          <div className="flex items-center gap-2">
                            {!comment.isResolved && (
                              <button className="text-xs" onClick={() => handleResolveComment(comment.id)} type="button">
                                Resolve
                              </button>
                            )}
                            {comment.isResolved && <div className="text-xs text-green-600">Resolved</div>}
                            {/* <button className="text-red-600 p-1" title="Delete comment" onClick={() => handleDeleteComment(comment.id)} type="button">
                              <Trash2 className="w-4 h-4" />
                            </button> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="px-3 py-2 border-t flex items-center gap-2">
                <input
                  placeholder="Write a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="flex-1 border py-2 px-3 rounded text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                />
                <button className="px-3 py-2 bg-purple-700 text-white rounded text-sm" onClick={handleAddComment} disabled={!commentText.trim()}>
                  Send
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="col-span-3 flex flex-col gap-3">
          <div className="border rounded p-2 bg-white">
            <div className="text-sm font-medium mb-2">Details</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <User className="w-4 h-4 text-gray-400" /> Uploaded:
              </div>
              <div className="font-medium text-sm">{drawing.uploadedBy}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <FolderKanban className="w-4 h-4 text-gray-400" /> Project:
              </div>
              <div className="font-medium text-sm">{drawing.projectName}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <FileImage className="w-4 h-4 text-gray-400" /> Type:
              </div>
              <div className="font-medium text-sm">{drawing.fileType}</div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <User className="w-4 h-4 text-gray-400" /> Assignee:
              </div>
              <div className="font-medium text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex -space-x-2 overflow-hidden">
                    {(drawing.designMembers || []).length > 0 ? (
                      (drawing.designMembers || []).map((m) => (
                        <UserAvatar key={m.id} member={m} />
                      ))
                    ) : (
                      <span className="truncate text-gray-400">{designMembersLoading ? "Loading..." : "—"}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="p-1 border rounded bg-white shrink-0 disabled:opacity-60"
                    title="Add assignee"
                    onClick={() => setShowAddAssignee(true)}
                    disabled={!canDesignAdmin || orgMembersLoading}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-gray-400" /> Approver:
              </div>
              <div className="font-medium text-sm">
                <div className="flex -space-x-2 overflow-hidden">
                  {(drawing.approverMembers || []).length > 0 ? (
                    (drawing.approverMembers || []).map((m) => (
                      <UserAvatar key={m.id} member={m} />
                    ))
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border rounded p-3 bg-white">
            <div className="text-xs text-gray-500 mb-2">Actions</div>
            <div className="space-y-2">
              {!isLocked && (
                <button
                  className="w-full p-2 border rounded disabled:opacity-60"
                  disabled={isReplacing}
                  onClick={handleReplaceClick}
                >
                  {isReplacing ? "Replacing..." : "Replace File"}
                </button>
              )}
              {canDesignAdmin && (
                <>
                  <button
                    className="w-full p-2 border rounded"
                    onClick={handleApprove}
                    disabled={drawing.status === "Approved" || isLocked || isApproving || isLocking}
                  >
                    {isApproving ? "Approving..." : drawing.status === "Approved" || drawing.status === "Locked" ? "Approved" : "Approve"}
                  </button>
                  {!isDraft && drawing.status === "Approved" && (
                    <button
                      className="w-full p-2 bg-purple-700 text-white rounded disabled:opacity-60"
                      onClick={handleLock}
                      disabled={isLocking}
                    >
                      {isLocking ? "Locking..." : "Lock Design"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleReplaceSelected} />

          {openIssuesCount > 0 && (
            <div className="border rounded p-2 bg-red-50">
              <div className="text-xs text-red-600 font-semibold">{openIssuesCount} Pending Issue(s)</div>
              <div className="text-xs text-gray-500">Resolve before locking</div>
            </div>
          )}

          <button className="w-full p-2 border rounded" onClick={() => setShowVersionHistory(true)}>
            Version History
          </button>
        </aside>
      </div>

      {/* Add assignee modal */}
      {showAddAssignee && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Add Assignee</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddAssignee(false);
                  setAssigneeToAdd("");
                }}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <select
              value={assigneeToAdd}
              onChange={(e) => setAssigneeToAdd(e.target.value)}
              className="w-full border p-2 rounded"
            >
              <option value="">{orgMembersLoading ? "Loading members..." : "Select member"}</option>
              {(orgMembers || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {displayNameFromOrgMember(m)}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() => {
                  setShowAddAssignee(false);
                  setAssigneeToAdd("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1 bg-purple-700 text-white rounded disabled:opacity-60"
                onClick={handleAddAssignee}
                disabled={!assigneeToAdd || addingAssignee}
              >
                {addingAssignee ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPinDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <h3 className="font-semibold mb-2">Add Comment Pin</h3>
            <textarea
              placeholder="Enter your comment or issue description..."
              value={newPinComment}
              onChange={(e) => setNewPinComment(e.target.value)}
              rows={3}
              className="w-full border p-2 rounded"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                className="px-3 py-1 border rounded"
                onClick={() => {
                  setShowPinDialog(false);
                  setPendingPin(null);
                  setNewPinComment("");
                }}
              >
                Cancel
              </button>
              <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={handleAddPin} disabled={!newPinComment.trim() || isCreatingPin}>
                {isCreatingPin ? "Adding..." : "Add Pin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-lg">
            <h3 className="font-semibold mb-2">Version History</h3>
            <div className="space-y-2 max-h-80 overflow-auto">
              {versionsLoading && <div className="text-sm text-gray-500 p-3">Loading versions...</div>}
              {!versionsLoading && versionsError && <div className="text-sm text-red-600 p-3">{versionsError}</div>}
              {!versionsLoading && !versionsError && (versions || []).length === 0 && (
                <div className="text-sm text-gray-500 p-3">No versions found.</div>
              )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).map((version) => (
                  <div key={version.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xs font-bold bg-gray-200 px-2 py-1 rounded shrink-0">{version.version}</div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{version.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {version.uploadedBy || drawing.uploadedBy} • {version.uploadDate || drawing.lastUpdated}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(version.status)}`}>{version.status}</span>
                      <button
                        className="px-2 py-1 text-sm border rounded disabled:opacity-60"
                        disabled={!version.fileUrl}
                        onClick={() => downloadDiagram(version.fileUrl, `${drawing.name}_${version.version}`)}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex justify-end mt-3">
              <button className="px-3 py-1 border rounded" onClick={() => setShowVersionHistory(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded">
            <p>Are you sure you want to delete this design?</p>
            <div className="flex justify-end gap-2 mt-2">
              <button className="px-3 py-1 border rounded" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="px-3 py-1 bg-red-600 text-white rounded" onClick={handleConfirmDeleteDesign}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PdfViewerSurface({
  drawing,
  pdfFile,
  pdfPage,
  pdfError,
  setPdfError,
  isPinMode,
  onSurfaceClick,
  pins,
  activePinId,
  setActivePinId,
  handleResolvePin,
}) {
  const { ref, width } = useResizeWidth();

  // react-pdf needs a numeric width; clamp to something reasonable
  const pageWidth = Math.max(320, Math.floor((width || 0) - 32));

  return (
    <div className="w-full h-full bg-gray-50 ">
      <div ref={ref} className="w-full h-full p-4">
        <div className="w-full flex justify-center">
          <div
            className={`relative inline-block bg-white border rounded ${isPinMode ? "cursor-crosshair" : ""}`}
            onClick={onSurfaceClick}
          >
            <Document
              file={pdfFile}
              loading={<div className="text-sm text-gray-500 p-6">Loading PDF…</div>}
              error={
                <div className="text-sm text-red-600 p-6">
                  Failed to load PDF
                  {pdfError ? <div className="text-xs text-red-500 mt-2 break-words">{pdfError}</div> : null}
                </div>
              }
              onLoadSuccess={() => setPdfError(null)}
              onLoadError={(err) => setPdfError(err?.message || String(err))}
              onSourceError={(err) => setPdfError(err?.message || String(err))}
            >
              <Page pageNumber={pdfPage} width={pageWidth} />
            </Document>

            {(pins || []).map((pin) => (
              <div
                key={pin.id}
                className="absolute transform -translate-x-1/2 -translate-y-full z-50"
                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              >
                <button
                  className="relative"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePinId(activePinId === pin.id ? null : pin.id);
                  }}
                  type="button"
                  aria-label="Pin"
                >
                  <Pin className={`w-4 h-4 ${pin.status === "solved" ? "text-green-600" : "text-red-600"}`} />
                  {activePinId === pin.id && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 z-[100]">
                      <div className="bg-white border p-2 rounded shadow text-left">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-medium text-gray-700">Pin</div>
                            {pin.status === "solved" ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">SOLVED</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">PENDING</span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePinId(null);
                            }}
                            type="button"
                            aria-label="Close"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap mb-1.5">{pin.comment}</p>
                        <div className="text-[10px] text-gray-500 mt-1">{pin.author ? `By: ${pin.author}` : ""}</div>
                        {pin.status === "pending" && (
                          <button
                            className="w-full mt-2 p-1 bg-green-600 text-white rounded text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResolvePin(pin.id);
                            }}
                            type="button"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              </div>
            ))}

            {isPinMode && (
              <div className="absolute top-3 left-3 bg-purple-50 text-purple-700 px-2.5 py-1 rounded text-xs">
                Click to place a pin
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
